import { useState } from "react";
import { MoreVertical, Reply, Copy, Trash2, RotateCcw, Clock, CheckCheck, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import VoiceMessage from "./VoiceMessage";

interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

interface Message {
  id: string;
  content: string;
  sender_id: string;
  receiver_id: string;
  created_at: string;
  is_read: boolean;
  message_type: string | null;
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  duration: number | null;
  thumbnail_url: string | null;
  location_data: any;
  isPending?: boolean;
  isError?: boolean;
  retryCount?: number;
}

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  showAvatar: boolean;
  reactions: MessageReaction[];
  onReaction: (messageId: string, emoji: string) => void;
  onRetry?: (messageId: string) => void;
  partnerName: string;
  partnerPhoto?: string;
}

const MessageBubble = ({ 
  message, 
  isOwn, 
  showAvatar, 
  reactions, 
  onReaction, 
  onRetry,
  partnerName, 
  partnerPhoto 
}: MessageBubbleProps) => {
  const [showReactions, setShowReactions] = useState(false);

  const commonEmojis = ['❤️', '👍', '😂', '😮', '😢', '😡'];

  const groupedReactions = reactions.reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = [];
    }
    acc[reaction.emoji].push(reaction);
    return acc;
  }, {} as { [emoji: string]: MessageReaction[] });

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const getMessageStatus = () => {
    if (message.isPending) {
      return <Clock className="w-3 h-3 text-ice-white/40" />;
    }
    if (message.isError) {
      return <AlertCircle className="w-3 h-3 text-neon-red" />;
    }
    if (isOwn) {
      return message.is_read ? (
        <CheckCheck className="w-3 h-3 text-toxic-green" />
      ) : (
        <Check className="w-3 h-3 text-ice-white/40" />
      );
    }
    return null;
  };

  const renderAvatar = () => {
    if (isOwn || !showAvatar) return null;
    
    return (
      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2">
        {partnerPhoto ? (
          <img
            src={partnerPhoto}
            alt={partnerName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-ice-white/20 flex items-center justify-center">
            <span className="text-xs text-ice-white">
              {partnerName[0] || "P"}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderMessageContent = () => {
    if (message.message_type === 'system') {
      return (
        <p className="text-xs text-ice-white/70 italic text-center">
          {partnerName} {message.content}
        </p>
      );
    }

    if (message.message_type === 'image' && message.file_url) {
      return (
        <div className="max-w-xs">
          <img
            src={message.file_url}
            alt={message.file_name || "Image"}
            className="rounded-lg w-full h-auto"
          />
          {message.content && (
            <p className="mt-2 text-sm">{message.content}</p>
          )}
        </div>
      );
    }

    if (message.message_type === 'voice' && message.file_url) {
      return (
        <VoiceMessage 
          audioUrl={message.file_url}
          duration={message.duration || undefined}
          transcription={message.content.startsWith('🎤') ? undefined : message.content}
          isOwn={isOwn}
        />
      );
    }

    if (message.message_type === 'file' && message.file_url) {
      return (
        <div className="flex items-center space-x-2 p-2 bg-ice-white/10 rounded-lg max-w-xs">
          <div className="flex-1">
            <p className="text-sm font-medium">{message.file_name}</p>
            <p className="text-xs text-ice-white/60">
              {message.file_size ? `${Math.round(message.file_size / 1024)}KB` : 'File'}
            </p>
          </div>
        </div>
      );
    }

    return <p className="text-sm whitespace-pre-wrap">{message.content}</p>;
  };

  // Special rendering for system messages
  if (message.message_type === 'system') {
    return (
      <div className="flex justify-center mb-2">
        <div className="bg-ice-white/5 border border-ice-white/10 rounded-full px-4 py-2 max-w-xs">
          {renderMessageContent()}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex w-full ${isOwn ? 'justify-end' : 'justify-start'} mb-1 group`}>
      {!isOwn && renderAvatar()}
      
      <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-last' : 'order-first'}`}>
        <div
          className={`px-4 py-2 rounded-2xl relative transition-all duration-200 ${
            message.isPending 
              ? 'opacity-70 scale-95' 
              : message.isError 
                ? 'bg-neon-red/30 border border-neon-red/50 text-ice-white' 
                : isOwn
                  ? 'bg-neon-red text-ice-white rounded-br-md'
                  : 'bg-ice-white/10 text-ice-white rounded-bl-md border border-ice-white/20'
          }`}
          onDoubleClick={() => setShowReactions(true)}
        >
          {renderMessageContent()}
          
          <div className={`flex items-center justify-between text-xs mt-1 ${
            message.isError ? 'text-neon-red/70' : isOwn ? 'text-ice-white/70' : 'text-ice-white/70'
          }`}>
            <span>{formatTime(message.created_at)}</span>
            <div className="flex items-center space-x-1">
              {getMessageStatus()}
              {message.isError && onRetry && (
                <button
                  onClick={() => onRetry(message.id)}
                  className="ml-2 text-neon-red hover:text-neon-red/80"
                  title="Retry sending"
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Message options */}
          <div className={`absolute top-0 ${isOwn ? '-left-8' : '-right-8'} opacity-0 group-hover:opacity-100 transition-opacity`}>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 bg-carbon/80 hover:bg-carbon text-ice-white">
                  <MoreVertical className="w-3 h-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-2 bg-carbon border-ice-white/20 text-ice-white">
                <div className="space-y-1">
                  <Button variant="ghost" size="sm" className="w-full justify-start text-ice-white hover:bg-ice-white/10">
                    <Reply className="w-3 h-3 mr-2" />
                    Reply
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-start text-ice-white hover:bg-ice-white/10"
                    onClick={copyToClipboard}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copy
                  </Button>
                  {message.isError && onRetry && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full justify-start text-toxic-green hover:bg-ice-white/10"
                      onClick={() => onRetry(message.id)}
                    >
                      <RotateCcw className="w-3 h-3 mr-2" />
                      Retry
                    </Button>
                  )}
                  {isOwn && (
                    <Button variant="ghost" size="sm" className="w-full justify-start text-neon-red hover:bg-ice-white/10">
                      <Trash2 className="w-3 h-3 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Reactions */}
        {Object.keys(groupedReactions).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(groupedReactions).map(([emoji, reactionList]) => (
              <button
                key={emoji}
                onClick={() => onReaction(message.id, emoji)}
                className="flex items-center space-x-1 bg-carbon border border-ice-white/20 rounded-full px-2 py-0.5 hover:bg-ice-white/10 text-xs transition-colors text-ice-white"
              >
                <span>{emoji}</span>
                <span>{reactionList.length}</span>
              </button>
            ))}
          </div>
        )}

        {/* Quick reactions */}
        {showReactions && (
          <div className="flex space-x-1 mt-2 bg-carbon border border-ice-white/20 rounded-full px-2 py-1 shadow-lg animate-fade-in">
            {commonEmojis.map((emoji) => (
              <button
                key={emoji}
                onClick={() => {
                  onReaction(message.id, emoji);
                  setShowReactions(false);
                }}
                className="text-lg hover:scale-110 transition-transform"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;